import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { OverviewPage } from './pages/OverviewPage';
import { BillingPage } from './pages/BillingPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { SettingsPage } from './pages/SettingsPage';
import { AppsPage } from './pages/AppsPage';
import { AuditPage } from './pages/AuditPage';
import { SellGpuPage } from './pages/SellGpuPage';
import { BuyGpuPage } from './pages/BuyGpuPage';
import { LoggedOutPage } from './pages/LoggedOutPage';
import { startSsoLogin } from './utils/ssoAuth';

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        <p className="text-foreground-secondary text-sm">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!isLoading && !user && !redirecting) {
      setRedirecting(true);
      const returnTo = window.location.pathname + window.location.search;
      startSsoLogin(returnTo === '/auth/callback' ? '/' : returnTo);
    }
  }, [user, isLoading, redirecting]);

  if (isLoading || redirecting || !user) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user || user.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/logged-out" element={<LoggedOutPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="apps" element={<AppsPage />} />
        <Route path="buy-gpu" element={<BuyGpuPage />} />
        <Route path="sell-gpu" element={<SellGpuPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route
          path="audit"
          element={
            <AdminRoute>
              <AuditPage />
            </AdminRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
